import {
  getAmenityDetails,
  getBuildingDetails,
  getPropertyDetails,
  getReservationList,
  getResourceAccess,
  getTenantInfo,
} from "~/services/server";
import { FormattedAmenityData, LoaderData, Reservation } from "~/types";
import { parseDayTime } from "~/utils";
import dayjs from "dayjs";

export const getFormattedAmenityData = async ({
  amenityID,
  forceReload,
  userID,
}: {
  amenityID: string;
  forceReload: boolean;
  userID?: string;
}): Promise<LoaderData<FormattedAmenityData>> => {
  const amenityDetails = await getAmenityDetails(amenityID);
  const propertyDetails = await getPropertyDetails(
    amenityDetails.data.propertyID,
    forceReload
  );

  const buildingDetails = await getBuildingDetails(
    amenityDetails.data.buildingID ?? ""
  );

  const reservationList = await getReservationList({
    amenityID: amenityDetails.data.id,
  });

  const reservationPromises = reservationList.data.map(
    async (value): Promise<Reservation> => {
      // TODO: authUID is currently not populated in the database
      const tenantDetails = (await getTenantInfo({ authUID: value.tenantID }))
        .data;
      return {
        timeFrame: {
          start: parseDayTime(value.startDate),
          end: parseDayTime(value.endDate),
        },
        tenantName: `${tenantDetails.lastName}, ${tenantDetails.firstName}`,
        createdAt: dayjs(value.createdAt),
        data: value,
      };
    }
  );

  const reservations = await Promise.all(reservationPromises).catch((error) => {
    console.error("error when resolving reservation", error);
    return [];
  });

  // Fetch access rights if userID is provided
  let accessRights = undefined;
  if (userID) {
    const accessRightsResult = await getResourceAccess({
      userID,
      resourceID: amenityID,
      forceReload,
    });
    accessRights = accessRightsResult.data;
  }

  return {
    success: amenityDetails.success && propertyDetails.success,
    data: {
      amenityDetails: amenityDetails.data,
      propertyDetails: propertyDetails.data,
      buildingDetails: buildingDetails.data,
      reservations,
      accessRights,
    },
    error:
      amenityDetails.error || buildingDetails.error || propertyDetails.error,
  };
};
